import React from 'react';
import { Box, Text, UnstyledButton } from '@mantine/core';
import { useMobileBreakpoint } from '@repo/utils';

/**
 * A single tab in the mobile secondary tab bar, including its value, display label, and disabled state.
 */
export interface MobileSubTab {
  value: string;
  label: string;
  disabled?: boolean;
}

interface MobileSubTabBarProps {
  tabs: MobileSubTab[];
  activeTab: string | null;
  onChange: (value: string) => void;
  /** CSS bottom offset. Defaults to the full MobileFooterNav height (content + safe-area spacer). */
  bottomOffset?: string | number;
}

/**
 * A fixed-position secondary tab bar that appears above the root mobile nav bar.
 * Renders nothing on desktop — use alongside a normal Tabs.List hidden via `!isMobile`.
 */
export function MobileSubTabBar({ tabs, activeTab, onChange, bottomOffset = 'calc(60px + env(safe-area-inset-bottom, 0px))' }: MobileSubTabBarProps) {
  const isMobile = useMobileBreakpoint();
  const MOBILE_TAB_LABEL_MAX_LENGTH = 9;

  if (!isMobile) return null;

  const parseTabLabel = (label: string) => {
    const match = label.match(/^(.*?)(\s*\((\d+)\))$/);

    if (!match) {
      return { primaryLabel: label, countLabel: null };
    }

    const [, primaryLabel = label, countLabel] = match;

    return {
      primaryLabel: primaryLabel.trim(),
      countLabel: countLabel?.trim() ?? null,
    };
  };

  const truncatePrimaryLabel = (label: string) => {
    if (label.length <= MOBILE_TAB_LABEL_MAX_LENGTH) {
      return label;
    }

    return `${label.slice(0, MOBILE_TAB_LABEL_MAX_LENGTH)}...`;
  };

  return (
    <Box
      component='nav'
      aria-label='Section tabs'
      style={{
        position: 'fixed',
        bottom: bottomOffset,
        left: 0,
        right: 0,
        zIndex: 140, // above footer nav (100) but below the AppShell navbar (175)
        minHeight: 48,
        borderTop: '1px solid var(--mantine-color-default-border)',
        backgroundColor: 'var(--mantine-color-body)',
        display: 'flex',
        alignItems: 'stretch',
        overflow: 'hidden',
      }}
    >
      {tabs.map(({ value, label, disabled }) => {
        const isActive = activeTab === value;
        const { primaryLabel, countLabel } = parseTabLabel(label);
        const displayLabel = truncatePrimaryLabel(primaryLabel);
        return (
          <UnstyledButton
            key={value}
            onClick={() => !disabled && onChange(value)}
            aria-selected={isActive}
            aria-disabled={disabled}
            style={{
              flex: '1 1 0',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingInline: 6,
              paddingBlock: 6,
              borderBottom: isActive
                ? '2px solid var(--mantine-color-blue-filled)'
                : '2px solid transparent',
              color: disabled
                ? 'var(--mantine-color-dimmed)'
                : isActive
                  ? 'var(--mantine-color-blue-filled)'
                  : 'var(--mantine-color-text)',
              opacity: disabled ? 0.45 : 1,
              transition: 'color 150ms ease, border-color 150ms ease',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <Text
              size='xs'
              fw={isActive ? 600 : 400}
              ta='center'
              title={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                minWidth: 0,
                lineHeight: 1.15,
              }}
            >
              <Box
                component='span'
                style={{
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {displayLabel}
              </Box>
              {countLabel && (
                <Box
                  component='span'
                  style={{
                    flexShrink: 0,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {countLabel}
                </Box>
              )}
            </Text>
          </UnstyledButton>
        );
      })}
    </Box>
  );
}

export default MobileSubTabBar;
